import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, Plus, Pencil, Trash2, X } from "lucide-react";
import { BRAND } from "../data/siteData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ax = axios.create({ baseURL: API, withCredentials: true });

const EMPTY_POST = {
  title: "", slug: "", category: "Coating Education", tags: [], excerpt: "", content: "",
  image: "", image_alt: "", meta_title: "", meta_description: "", focus_keyword: "", read_time: 5, published: true, faqs: [],
};

const inputCls = "w-full bg-white border border-gray-300 text-[#000000] p-2.5 text-sm outline-none focus:ring-2 focus:ring-[#028090]";
const labelCls = "block text-xs uppercase tracking-wider font-semibold text-gray-500 mb-1";

function PostEditor({ post, onClose, onSaved }) {
  const [form, setForm] = useState({ ...EMPTY_POST, ...post, tags: post?.tags || [] });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    setSaving(true);
    const payload = { ...form, tags: Array.isArray(form.tags) ? form.tags : form.tags.split(",").map((t) => t.trim()).filter(Boolean), read_time: Number(form.read_time) || 5 };
    delete payload.id; delete payload._id; delete payload.author_name; delete payload.author_bio; delete payload.created_at;
    try {
      if (post?.id) await ax.put(`/posts/${post.id}`, payload);
      else await ax.post("/posts", payload);
      toast.success("Post saved");
      onSaved();
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center p-4 overflow-y-auto" data-testid="post-editor-modal">
      <div className="bg-white w-full max-w-3xl p-8 my-8">
        <div className="flex justify-between items-center mb-6">
          <h2 className="font-display text-xl font-bold text-[#000000]">{post?.id ? "Edit Post" : "New Post"}</h2>
          <button onClick={onClose} data-testid="post-editor-close" aria-label="Close"><X size={20} /></button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2"><label className={labelCls}>Title</label><input data-testid="editor-title" className={inputCls} value={form.title} onChange={set("title")} /></div>
          <div><label className={labelCls}>Slug</label><input data-testid="editor-slug" className={inputCls} value={form.slug} onChange={set("slug")} /></div>
          <div><label className={labelCls}>Category</label><input data-testid="editor-category" className={inputCls} value={form.category} onChange={set("category")} /></div>
          <div><label className={labelCls}>Tags (comma separated)</label><input data-testid="editor-tags" className={inputCls} value={Array.isArray(form.tags) ? form.tags.join(", ") : form.tags} onChange={set("tags")} /></div>
          <div><label className={labelCls}>Read time (min)</label><input data-testid="editor-read-time" type="number" className={inputCls} value={form.read_time} onChange={set("read_time")} /></div>
          <div className="sm:col-span-2"><label className={labelCls}>Excerpt</label><textarea data-testid="editor-excerpt" className={inputCls} rows={2} value={form.excerpt} onChange={set("excerpt")} /></div>
          <div><label className={labelCls}>Image URL</label><input data-testid="editor-image" className={inputCls} value={form.image} onChange={set("image")} /></div>
          <div><label className={labelCls}>Image Alt Text</label><input data-testid="editor-image-alt" className={inputCls} value={form.image_alt} onChange={set("image_alt")} /></div>
          <div><label className={labelCls}>Meta Title</label><input data-testid="editor-meta-title" className={inputCls} value={form.meta_title} onChange={set("meta_title")} /></div>
          <div><label className={labelCls}>Focus Keyword</label><input data-testid="editor-focus-keyword" className={inputCls} value={form.focus_keyword} onChange={set("focus_keyword")} /></div>
          <div className="sm:col-span-2"><label className={labelCls}>Meta Description</label><textarea data-testid="editor-meta-description" className={inputCls} rows={2} value={form.meta_description} onChange={set("meta_description")} /></div>
          <div className="sm:col-span-2"><label className={labelCls}>Content (Markdown)</label><textarea data-testid="editor-content" className={`${inputCls} font-mono text-xs`} rows={14} value={form.content} onChange={set("content")} /></div>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" data-testid="editor-published" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} /> Published
          </label>
        </div>
        <div className="flex gap-3 mt-6">
          <button onClick={save} disabled={saving} data-testid="editor-save-btn" className="bg-[#028090] text-white px-6 py-3 uppercase font-bold tracking-wider text-sm hover:bg-[#026c7a] disabled:opacity-60">{saving ? "Saving…" : "Save Post"}</button>
          <button onClick={onClose} className="border border-gray-300 px-6 py-3 uppercase font-bold tracking-wider text-sm text-gray-600">Cancel</button>
        </div>
      </div>
    </div>
  );
}

export default function AdminPage() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [creds, setCreds] = useState({ email: "", password: "" });
  const [tab, setTab] = useState("leads");
  const [leads, setLeads] = useState([]);
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(null);

  const loadData = useCallback(() => {
    ax.get("/leads").then((r) => setLeads(r.data)).catch(() => {});
    ax.get("/posts?include_unpublished=true").then((r) => setPosts(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    ax.get("/auth/me").then((r) => { setUser(r.data); }).catch(() => {}).finally(() => setChecking(false));
  }, []);
  useEffect(() => { if (user) loadData(); }, [user, loadData]);

  const login = async (e) => {
    e.preventDefault();
    try {
      const { data } = await ax.post("/auth/login", creds);
      setUser(data);
      toast.success("Welcome back");
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Login failed");
    }
  };

  const logout = async () => { await ax.post("/auth/logout"); setUser(null); };

  const deletePost = async (p) => {
    if (!window.confirm(`Delete "${p.title}"?`)) return;
    await ax.delete(`/posts/${p.id}`);
    toast.success("Post deleted");
    loadData();
  };

  if (checking) return <div className="min-h-screen bg-[#000000] flex items-center justify-center text-gray-400">Loading…</div>;

  if (!user) {
    return (
      <div className="min-h-screen bg-[#000000] flex items-center justify-center p-6">
        <form onSubmit={login} className="bg-[#0a0a0a] border border-white/10 p-10 w-full max-w-sm teal-glow" data-testid="admin-login-form">
          <img src="https://customer-assets.emergentagent.com/job_phoenix-flooring-pro/artifacts/toplf27o_Logo%202-04.png" alt="Vantyx Coatings" className="h-14 w-auto mb-4" />
          <h1 className="font-display text-2xl font-bold text-white mb-1">Admin Portal</h1>
          <p className="text-gray-500 text-sm mb-7">Sign in to manage posts & leads</p>
          <label className="block text-xs uppercase tracking-wider font-semibold text-gray-400 mb-1.5">Email</label>
          <input data-testid="admin-email-input" type="email" className="w-full bg-white/5 border border-white/20 text-white p-3 mb-4 outline-none focus:ring-2 focus:ring-[#028090] text-sm" value={creds.email} onChange={(e) => setCreds({ ...creds, email: e.target.value })} />
          <label className="block text-xs uppercase tracking-wider font-semibold text-gray-400 mb-1.5">Password</label>
          <input data-testid="admin-password-input" type="password" className="w-full bg-white/5 border border-white/20 text-white p-3 mb-6 outline-none focus:ring-2 focus:ring-[#028090] text-sm" value={creds.password} onChange={(e) => setCreds({ ...creds, password: e.target.value })} />
          <button type="submit" data-testid="admin-login-button" className="w-full bg-[#028090] text-white py-3.5 uppercase font-bold tracking-wider text-sm hover:bg-[#026c7a]">Sign In</button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <header className="bg-[#000000] px-6 py-4 flex justify-between items-center">
        <div className="flex items-center gap-3">
          <img src="https://customer-assets.emergentagent.com/job_phoenix-flooring-pro/artifacts/toplf27o_Logo%202-04.png" alt="Vantyx Coatings" className="h-10 w-auto" />
          <span className="text-white/60 text-sm uppercase tracking-widest font-semibold">Admin</span>
        </div>
        <button onClick={logout} data-testid="admin-logout-button" className="flex items-center gap-2 text-gray-300 text-sm hover:text-white"><LogOut size={16} /> Logout</button>
      </header>
      <div className="max-w-6xl mx-auto p-6">
        <div className="flex gap-3 mb-8">
          <button onClick={() => setTab("leads")} data-testid="admin-tab-leads" className={`px-5 py-2.5 text-sm font-bold uppercase tracking-wider ${tab === "leads" ? "bg-[#028090] text-white" : "bg-white text-gray-600 border border-gray-300"}`}>Leads ({leads.length})</button>
          <button onClick={() => setTab("posts")} data-testid="admin-tab-posts" className={`px-5 py-2.5 text-sm font-bold uppercase tracking-wider ${tab === "posts" ? "bg-[#028090] text-white" : "bg-white text-gray-600 border border-gray-300"}`}>Blog Posts ({posts.length})</button>
        </div>

        {tab === "leads" && (
          <div className="bg-white border border-gray-200 overflow-x-auto" data-testid="admin-leads-table">
            <table className="w-full text-sm">
              <thead><tr className="bg-[#000000] text-white text-left">
                <th className="p-3 font-semibold">Date</th><th className="p-3 font-semibold">Name</th><th className="p-3 font-semibold">Phone</th><th className="p-3 font-semibold">Email</th><th className="p-3 font-semibold">City</th><th className="p-3 font-semibold">Garage</th><th className="p-3 font-semibold">Source</th><th className="p-3 font-semibold">Page</th>
              </tr></thead>
              <tbody>
                {leads.map((l) => (
                  <tr key={l.id} className="border-b border-gray-100">
                    <td className="p-3 text-gray-500 whitespace-nowrap">{new Date(l.created_at).toLocaleDateString()}</td>
                    <td className="p-3 font-semibold text-[#000000]">{l.name}</td>
                    <td className="p-3">{l.phone}</td><td className="p-3">{l.email}</td>
                    <td className="p-3">{l.city || "—"}</td><td className="p-3">{l.garage_size || "—"}</td>
                    <td className="p-3">{l.hear_about || "—"}</td><td className="p-3 text-gray-400 text-xs">{l.source_page || "—"}</td>
                  </tr>
                ))}
                {leads.length === 0 && <tr><td colSpan={8} className="p-8 text-center text-gray-400">No leads yet</td></tr>}
              </tbody>
            </table>
          </div>
        )}

        {tab === "posts" && (
          <div>
            <button onClick={() => setEditing({})} data-testid="admin-new-post-btn" className="flex items-center gap-2 bg-[#028090] text-white px-5 py-3 uppercase font-bold tracking-wider text-sm mb-6 hover:bg-[#026c7a]"><Plus size={16} /> New Post</button>
            <div className="space-y-3">
              {posts.map((p) => (
                <div key={p.id} className="bg-white border border-gray-200 p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid={`admin-post-row-${p.slug}`}>
                  <div>
                    <p className="font-semibold text-[#000000]">{p.title} {!p.published && <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 ml-2">Draft</span>}</p>
                    <p className="text-gray-400 text-xs mt-1">/{p.slug} · {p.category}</p>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => setEditing(p)} data-testid={`admin-edit-${p.slug}`} className="flex items-center gap-1.5 border border-gray-300 px-4 py-2 text-sm text-gray-600 hover:border-[#028090] hover:text-[#028090]"><Pencil size={14} /> Edit</button>
                    <button onClick={() => deletePost(p)} data-testid={`admin-delete-${p.slug}`} className="flex items-center gap-1.5 border border-red-200 px-4 py-2 text-sm text-red-500 hover:bg-red-50"><Trash2 size={14} /> Delete</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      {editing !== null && <PostEditor post={editing.id ? editing : null} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); loadData(); }} />}
    </div>
  );
}
